import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosInstance';
import useWafCaptcha from '../hooks/useWafCaptcha';
import useCapsLockWarning from '../hooks/useCapsLockWarning';
import CapsLockWarning from '../components/CapsLockWarning';
import { LanguageContext } from '../LanguageContext';

const COPY = {
  en: {
    skip: 'Skip to main content',
    heading: 'Create your account',
    intro: 'Create an account when you are ready to begin. We only ask for the basics here so you can access the portal securely.',
    email: 'Email address',
    password: 'Password',
    passwordHint: 'Use at least 8 characters with an upper and lower case letter, a number, and a symbol.',
    register: 'Create account',
    confirmHeading: 'Confirm your email to finish signing up',
    confirmExistingHeading: 'Finish confirming your account',
    confirmCode: 'Confirmation code',
    confirmButton: 'Confirm',
    resendCode: 'Resend code',
    returnToSignIn: 'Return to sign in',
    signInInstead: 'Sign in instead',
    resetPasswordInstead: 'Reset or set password',
    validation: {
      emailRequired: 'Email address is required.',
      emailInvalid: 'Enter a valid email address.',
      passwordRequired: 'Password is required.'
    },
    messages: {
      registrationFailed: 'Registration failed.',
      confirmationFailed: 'Confirmation failed.',
      accountConfirmed: 'Account confirmed. Please sign in.',
      registrationSuccess: 'Your account was created. Enter the confirmation code we emailed you to activate it. After confirmation you will be redirected to the sign-in page.',
      existingAccountNeedsConfirmation: 'This email already has an account that still needs confirmation. Enter your confirmation code below, or request a new code.',
      existingAccountConfirmed: 'An account with this email already exists. If you are already working with NWAC, your case manager may have set it up for you. Sign in if you know your password, or use Reset or set password to activate your access.',
      confirmIntro: 'Enter the confirmation code we emailed you. After confirmation you will be redirected to the sign-in page.',
      codeResent: 'If the account exists, a new confirmation code has been sent.'
    }
  },
  fr: {
    skip: 'Passer au contenu principal',
    heading: 'Créer votre compte',
    intro: "Créez un compte lorsque vous êtes prête à commencer. Nous demandons seulement les renseignements de base nécessaires pour vous donner un accès sécuritaire au portail.",
    email: 'Adresse courriel',
    password: 'Mot de passe',
    passwordHint: 'Utilisez au moins 8 caractères comprenant une majuscule, une minuscule, un chiffre et un symbole.',
    register: 'Créer le compte',
    confirmHeading: 'Confirmez votre courriel pour terminer l’inscription',
    confirmExistingHeading: 'Terminez la confirmation de votre compte',
    confirmCode: 'Code de confirmation',
    confirmButton: 'Confirmer',
    resendCode: 'Renvoyer le code',
    returnToSignIn: 'Retour à la connexion',
    signInInstead: 'Se connecter',
    resetPasswordInstead: 'Définir ou réinitialiser le mot de passe',
    validation: {
      emailRequired: "L'adresse courriel est obligatoire.",
      emailInvalid: 'Entrez une adresse courriel valide.',
      passwordRequired: 'Le mot de passe est obligatoire.'
    },
    messages: {
      registrationFailed: "L'inscription a échoué.",
      confirmationFailed: 'La confirmation a échoué.',
      accountConfirmed: 'Compte confirmé. Veuillez vous connecter.',
      registrationSuccess: "Votre compte a été créé. Entrez le code de confirmation envoyé par courriel pour l'activer. Après la confirmation, vous serez redirigée vers la page de connexion.",
      existingAccountNeedsConfirmation: 'Cette adresse courriel correspond déjà à un compte qui doit encore être confirmé. Saisissez votre code de confirmation ci-dessous ou demandez-en un nouveau.',
      existingAccountConfirmed: 'Un compte existe déjà pour cette adresse courriel. Si vous travaillez déjà avec l’AFAC, votre gestionnaire de cas l’a peut-être créé pour vous. Connectez-vous si vous connaissez votre mot de passe, ou utilisez Définir ou réinitialiser le mot de passe pour activer votre accès.',
      confirmIntro: "Entrez le code de confirmation envoyé par courriel. Après la confirmation, vous serez redirigée vers la page de connexion.",
      codeResent: "Si le compte existe, un nouveau code de confirmation a été envoyé."
    }
  }
};

function Register() {
  const { language } = useContext(LanguageContext);
  const text = COPY[language] || COPY.en;
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [confirmCode, setConfirmCode] = useState('');
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [existingAccountState, setExistingAccountState] = useState(null);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const errorRef = useRef(null);
  const confirmationInputRef = useRef(null);
  const { ensureCaptcha, CaptchaOverlay } = useWafCaptcha();
  const capsPassword = useCapsLockWarning();

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  useEffect(() => {
    if (awaitingConfirmation && confirmationInputRef.current) {
      confirmationInputRef.current.focus();
    }
  }, [awaitingConfirmation]);

  useEffect(() => {
    if (!awaitingConfirmation) return;
    if (typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [awaitingConfirmation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    if (existingAccountState) {
      setExistingAccountState(null);
      setConfirmationMessage('');
    }
    if (error) setError('');
  };

  function redirectToSignIn() {
    setExistingAccountState(null);
    setConfirmationMessage('');
    setAwaitingConfirmation(false);
    navigate('/login');
  }

  const validateForm = () => {
    const errors = {};
    if (!formData.email) errors.email = text.validation.emailRequired;
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = text.validation.emailInvalid;
    if (!formData.password) errors.password = text.validation.passwordRequired;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setError('');
    setExistingAccountState(null);
    setConfirmationMessage('');
    try {
      await ensureCaptcha();
      const payload = { ...formData, locale: language === 'fr' ? 'fr' : 'en' };
      const resp = await api.post('/api/auth/register', payload);
      if (resp.data.success) {
        if (resp.data.userConfirmed) {
          setConfirmationMessage(text.messages.accountConfirmed);
          redirectToSignIn();
        } else {
          setAwaitingConfirmation(true);
          setConfirmationMessage(text.messages.registrationSuccess);
        }
      } else {
        setError(resp.data.message || text.messages.registrationFailed);
      }
    } catch (err) {
      const serverCode = String(err.response?.data?.code || '').trim();
      const msg = err.response?.data?.message || text.messages.registrationFailed;
      const normalizedMsg = String(msg).toLowerCase();
      const isUnconfirmedExisting =
        serverCode === 'ACCOUNT_EXISTS_UNCONFIRMED';
      const isConfirmedExisting =
        serverCode === 'ACCOUNT_EXISTS_CONFIRMED' ||
        (
          !serverCode &&
          (normalizedMsg.includes('already exists') || normalizedMsg.includes('existe déjà') || normalizedMsg.includes('existe deja'))
        );

      if (isUnconfirmedExisting) {
        setError('');
        setExistingAccountState('unconfirmed');
        setAwaitingConfirmation(true);
        setConfirmationMessage(text.messages.existingAccountNeedsConfirmation);
        return;
      }

      if (isConfirmedExisting) {
        setError('');
        setExistingAccountState('confirmed');
        setConfirmationMessage(text.messages.existingAccountConfirmed);
        return;
      }

      setError(msg);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await ensureCaptcha();
      const resp = await api.post('/api/auth/confirm-registration', {
        email: formData.email.trim(),
        code: confirmCode.trim()
      });
      if (resp.data.success) {
        setConfirmationMessage(text.messages.accountConfirmed);
        redirectToSignIn();
      } else {
        setError(resp.data.message || text.messages.confirmationFailed);
      }
    } catch (err) {
      setError(err.response?.data?.message || text.messages.confirmationFailed);
    }
  };

  const showConfirmationModal = awaitingConfirmation;

  return (
    <>
      <CaptchaOverlay />
      {/* WCAG 2.4.1: Skip to main content link */}
      <a href="#main-content" className="govuk-skip-link">{text.skip}</a>
      <div>
        <main className="govuk-main-wrapper" id="main-content" role="main" tabIndex="-1">
          <div className="govuk-width-container">
            <h1 className="govuk-heading">{text.heading}</h1>
            <p className="govuk-body">{text.intro}</p>
            {error && !showConfirmationModal && (
                <p className="govuk-error-message" aria-live="assertive" tabIndex="-1" ref={errorRef}>
                  {error}
                </p>
            )}
            {confirmationMessage && !showConfirmationModal && existingAccountState !== 'confirmed' && (
              <div className="govuk-inset-text">{confirmationMessage}</div>
            )}
            {existingAccountState === 'confirmed' && !showConfirmationModal && (
              <div className="govuk-inset-text">
                <p className="govuk-body">{confirmationMessage}</p>
                <div className="govuk-button-group">
                  <a
                    href="/login"
                    className="govuk-button"
                    data-module="govuk-button"
                    onClick={(e) => {
                      e.preventDefault();
                      redirectToSignIn();
                    }}
                  >
                    {text.signInInstead}
                  </a>
                  <a href="/forgot-password" className="govuk-link" style={{ alignSelf: 'center' }}>
                    {text.resetPasswordInstead}
                  </a>
                </div>
              </div>
            )}
            {/* Registration Form Section */}
            <section className="govuk-section">
              {!awaitingConfirmation && (
                <form onSubmit={handleRegister} autoComplete="on">
                  <div className={`govuk-form-group ${validationErrors.email ? 'govuk-form-group--error' : ''}`}>
                    <label htmlFor="email-input" className="govuk-label">{text.email}</label>
                    {validationErrors.email && <span className="govuk-error-message">{validationErrors.email}</span>}
                    <input type="email" className="govuk-input" id="email-input" name="email" value={formData.email} onChange={handleInputChange} required autoComplete="email" />
                  </div>
                  <div className={`govuk-form-group ${validationErrors.password ? 'govuk-form-group--error' : ''}`}>
                    <label htmlFor="password" className="govuk-label">{text.password}</label>
                    {validationErrors.password && <span className="govuk-error-message">{validationErrors.password}</span>}
                    <input type="password" className="govuk-input" id="password" name="password" value={formData.password} onChange={handleInputChange} required autoComplete="new-password" {...capsPassword.bind} />
                    <div className="govuk-hint">{text.passwordHint}</div>
                    <CapsLockWarning show={capsPassword.show} language={language} />
                  </div>
                  <div className="govuk-button-group">
                    <button type="submit" className="govuk-button govuk-button--start">{text.register}</button>
                  </div>
                </form>
              )}
            </section>
          </div>
        </main>
      </div>
      {showConfirmationModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirmation-modal-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '6px',
              maxWidth: '32rem',
              width: '100%',
              padding: '2rem',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}
          >
            <h2 className="govuk-heading-m" id="confirmation-modal-title">
              {existingAccountState === 'unconfirmed' ? text.confirmExistingHeading : text.confirmHeading}
            </h2>
            <p className="govuk-body" aria-live="polite">
              {confirmationMessage || text.messages.confirmIntro}
            </p>
            {error && (
              <p className="govuk-error-message" aria-live="assertive" tabIndex="-1" ref={errorRef}>
                {error}
              </p>
            )}
            <form onSubmit={handleConfirm} autoComplete="off">
              <div className="govuk-form-group">
                <label htmlFor="confirmCode" className="govuk-label">{text.confirmCode}</label>
                <input
                  type="text"
                  className="govuk-input"
                  id="confirmCode"
                  name="confirmCode"
                  value={confirmCode}
                  onChange={(e)=>setConfirmCode(e.target.value)}
                  required
                  ref={confirmationInputRef}
                />
              </div>
              <div className="govuk-button-group">
                <button type="submit" className="govuk-button">{text.confirmButton}</button>
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary"
                  onClick={async ()=>{
                    try {
                      await ensureCaptcha();
                      const r = await api.post('/api/auth/resend-confirmation', { email: formData.email });
                      setConfirmationMessage(r.data.message || text.messages.codeResent);
                    } catch(_) {
                      setConfirmationMessage(text.messages.codeResent);
                    }
                  }}
                >
                  {text.resendCode}
                </button>
                <a
                  href="/login"
                  className="govuk-link"
                  style={{ alignSelf: 'center' }}
                  onClick={(e)=>{
                    e.preventDefault();
                    redirectToSignIn();
                  }}
                >
                  {text.returnToSignIn}
                </a>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Register;
